/**
 * RuntimeListView — config-driven list/table view for App mode.
 *
 * Designed for very large datasets (tested up to ~500K rows): rows are fetched
 * in chunks of CHUNK_SIZE on demand, kept in a sparse LRU cache (≤ MAX_PAGES),
 * and rendered through TanStack Virtual so only ~30 visible rows live in the DOM.
 *
 * Full CRUD: Create/Update via onCreate/onOpen (workspace tabs), Read
 * (virtualized + search + sort), Delete (soft delete with undelete).
 */
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppLocale } from '@/app/permissions/useAppLocale'
import { resolveLabel, mergeAttributeSynonyms } from './resolveLabel'
import { subscribeRecordsChanged } from './recordsChanged'
import { deleteConfirmMessage } from './deleteConfirmMessage'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { ImportDialog } from './ImportDialog'
import { DocumentImportDialog } from './DocumentImportDialog'
import { FileDropOverlay } from './FileDropOverlay'
import { classifyImportForObject, IMPORT_ACCEPT, type ObjectImportKind } from './intakeFileRouting'
import { exportRowsToCsv, downloadCsv, exportCellValue, buildExportListParams } from './exportList'
import { ListSelectionBar } from './ListSelectionBar'
import { ListSettingsDialog } from './listSettings/ListSettingsDialog'
import type { ListSettingsTabId, ListSettingsValue } from './listSettings/types'
import { useSavedViews } from './useSavedViews'
import type { SavedView } from './useSavedViews'
import type { ConfigObject, AttributeDef } from '../designer/services/configObjectApi'
import { runtimeApi } from '@/app/services/runtimeApi'
import { useWindowedRows } from './useWindowedRows'
import { useDebouncedValue } from './useDebouncedValue'
import { formatListCellValue, type ListCellAttrHint } from '../shared/formatListCellValue'
import { hintFromColumn } from '@/shared/listCell'
import {
  columnsFromSchema,
  columnsFromAttributes,
  buildListFilters,
  commandBarFromSchema,
  defaultSortFromSchema,
  compositeFilterFields,
  type ListColumnSpec,
} from './listSchema'
import { splitCompositeTypes } from './CompositeFieldWidget'
import { useColumnPrefs } from './useColumnPrefs'
import { ColumnsMenu } from './ColumnsMenu'
import { ListFormTabsBar } from '@/designer/components/editors/FormEditor/elements/ListFormTabsBar'
import { TokenManager } from '@/utils/api'
import { logger } from '@/utils/logger'
import type { FormSchema } from '@/designer/types/form.types'
import {
  usePrintCommands,
  printCommandToOption,
  PrintLayoutPickerMenu,
  type PrintLayoutOption,
} from '@/runtime/print'
import { ListToolbar } from '@/shared/components'
import type { CommandItem } from '@/shared/components'
import { BaseFormShell } from '@/shared/forms'
import { prefsStore } from '@/app/preferences/prefsStore'
import {
  LOCKED_PRIMARY_IDS,
  commandPinKey,
  parsePinPrefs,
  resolvePinSlot,
  LIST_CHROME_IDS,
  type ListChromeId,
  type PinSlot,
} from '@/shared/form/commandPin'
import { useDensity } from '@/shared/density/DensityProvider'
import Snackbar from '@mui/material/Snackbar'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import {
  availableViews,
  kanbanCandidates,
  useViewPrefs,
  migrateViewKind,
  KanbanView,
  FlatListView,
} from '@/shared/listViews'
import { GroupBySettings, dropStale } from '@/shared/groupBy'
import type { ListViewKind, FlatListViewHandle } from '@/shared/listViews'
import { ListSurface } from '@/shared/operator'
import { confirmAsync } from '@/shared/dialogs'
import { resolveActiveListView } from './listViewMode'
import { useCappedRows, CAPPED_FETCH_LIMIT } from './useCappedRows'
import { useGroupTree } from './useGroupTree'
import { RuntimeGroupTreeView } from './RuntimeGroupTreeView'
import { useInlineEdit } from './useInlineEdit'
import { ListAggregatesFooter } from './ListAggregatesFooter'
import { computeClientAggregates } from './computeClientAggregates'
import { useListKeyboardNav } from './useListKeyboardNav'
import { useRowSelection } from './useRowSelection'
import { useHierarchyTree } from './useHierarchyTree'
import { RuntimeHierarchyTreeView } from './RuntimeHierarchyTreeView'
import { seedForCreate, type HierarchyCreateKind } from './hierarchyCreateSeed'
import { filterSelectionRoots, type SelectionRow } from './hierarchySelection'
import { hierarchyDeleteConfirmMessage } from './deleteConfirmMessage'
import { isPredefinedRecord } from './appHiddenAttributes'
import { isAppHiddenAttr } from './platformInternalAttrs'
import Menu from '@mui/material/Menu'

/**
 * Picker mode: the list is mounted inside RefChoiceDialog to choose a value.
 * Opening a row picks it; destructive and bulk commands are hidden so a
 * modal opened to select something can never delete or export.
 */
export interface ListSelectionMode {
  onPick: (id: string, record: Record<string, unknown>) => void
}

interface RuntimeListViewProps {
  connectionId: string
  objectName: string
  onOpen?: (recordId: string) => void
  /** Optional hierarchy create seed (parent_id / is_group). */
  onCreate?: (seed?: Record<string, unknown>) => void
  /** When set, the list acts as a value picker — see ListSelectionMode. */
  selectionMode?: ListSelectionMode
  /** Optional list-form schema — when its Table node declares columns, they drive the grid. */
  schema?: FormSchema | null
  /** True when a custom form existed but was broken and we fell back to the generated one. */
  degraded?: boolean
  /**
   * Effective rights on this object (Task 12, ERP Users & Access Rights
   * epic). Optional — omitted, Create and row/bulk Delete behave exactly as
   * before this epic (both available, subject only to the pre-existing
   * predefined-record guards).
   */
  permissions?: { canCreate: boolean; canDelete: boolean }
  /** Workspace tab already shows the object name — omit the in-page heading. */
  hideShellTitle?: boolean
}

type SortDir = 'asc' | 'desc'

/** Server page size + JS row chunk size. Smaller = more requests, less memory. */
const CHUNK_SIZE = 50
/** LRU cap on chunks held in memory. 20 × 50 = up to 1000 rows resident. */
const MAX_PAGES_IN_MEMORY = 20

/** Keystroke → refetch delay for the list search box. */
const SEARCH_DEBOUNCE_MS = 300

export const RuntimeListView: React.FC<RuntimeListViewProps> = ({
  connectionId,
  objectName,
  onOpen,
  onCreate,
  selectionMode,
  schema,
  degraded,
  permissions,
  hideShellTitle = false,
}) => {
  const picking = !!selectionMode
  const navigate = useNavigate()
  const { locale, defaultCode } = useAppLocale()
  const [config, setConfig] = useState<ConfigObject | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)
  // Hierarchical (parent_id/is_group) catalogs get a lazy tree instead of the
  // flat windowed list — hierarchy mode wins over group_by for v1 (spec §2.1).
  const hierarchical = !!config?.traits?.hierarchical
  // Import routing needs to know whether this object is a catalog (spreadsheet
  // import), a document (single-file recognition) or neither (import hidden) —
  // registers/enums/constants/sequences have no import flow.
  const objectKind: ObjectImportKind = useMemo(() => {
    if (config?.obj_type === 'catalog') return 'catalog'
    if (config?.obj_type === 'document') return 'document'
    return 'other'
  }, [config?.obj_type])
  const canImport = !picking && objectKind !== 'other' && (permissions?.canCreate ?? true)
  // `searchInput` is what the box shows (instant); `search` is what the data
  // hooks see. Every one of them (windowed rows, hierarchy, kanban, group tree,
  // Σ aggregates, export) disposes its cache and refetches from page one when
  // this changes — so keystrokes must not reach them raw.
  const [searchInput, setSearch] = useState('')
  const search = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)
  const defaultSort = defaultSortFromSchema(schema)
  const [sortField, setSortField] = useState(defaultSort?.field ?? '')
  const [sortDir, setSortDir] = useState<SortDir>(defaultSort?.dir ?? 'asc')
  const { density, setDensity, tokens } = useDensity()
  const rowHeight = tokens.listRowHeight
  const [showDeleted, setShowDeleted] = useState(false)
  const { prefs, update: updatePrefs } = useViewPrefs(`${connectionId}:${objectName}`, {
    hierarchical,
    defaultView: hierarchical ? 'tree' : 'list',
  })
  const saved = useSavedViews(connectionId, objectName)
  const selection = useRowSelection(`${connectionId}:${objectName}`)
  const [moveError, setMoveError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [pinRevision, setPinRevision] = useState(0)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsTab, setSettingsTab] = useState<ListSettingsTabId>('appearance')
  const [settingsEpoch, setSettingsEpoch] = useState(0)
  const lastSettingsTab = useRef<ListSettingsTabId>('appearance')

  const openSettings = (tab?: ListSettingsTabId) => {
    const next = tab ?? lastSettingsTab.current
    lastSettingsTab.current = next
    setSettingsTab(next)
    setSettingsOpen(true)
  }

  // Schema-declared tabs (category filter chips) + filter_fields (dropdown equality filters).
  const [tabIndex, setTabIndex] = useState(0)
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})
  const [filterOptionsByField, setFilterOptionsByField] = useState<
    Record<string, Array<{ value: string; label: string }>>
  >({})

  // Reset tab/filter/sort selection when the object or its list schema changes so stale
  // filters and sort from a previous object/form don't leak into the next one. Re-apply
  // the schema default sort (if any).
  useEffect(() => {
    setTabIndex(0)
    setFilterValues({})
    setFilterOptionsByField({})
    const newDefaultSort = defaultSortFromSchema(schema)
    setSortField(newDefaultSort?.field ?? '')
    setSortDir(newDefaultSort?.dir ?? 'asc')
    // schema?.id — identity of the list form; full `schema` is a new object each resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectName, schema?.id])

  // Async options for filter_fields with an `options_endpoint` (e.g. GET /v1/app/{connectionId}/{ObjectName}/lookup).
  useEffect(() => {
    const fields = schema?.filter_fields?.filter((f) => f.options_endpoint) ?? []
    if (!fields.length) return
    const token = TokenManager?.getAccessToken?.()
    if (!token) return
    for (const ff of fields) {
      const ep = ff.options_endpoint!
      void (async () => {
        try {
          const r = await fetch(ep, { headers: { Authorization: `Bearer ${token}` } })
          if (!r.ok) return
          const data = await r.json()
          const arr = Array.isArray(data) ? data : data.data ?? data.items ?? []
          const mapped = (arr as Array<{ id?: string; code?: string; label?: string }>).map((x) => ({
            value: String(x.id ?? ''),
            label: String(x.label ?? x.code ?? x.id ?? ''),
          }))
          setFilterOptionsByField((prev) => ({ ...prev, [ff.name]: mapped }))
        } catch (e) {
          logger.error('RuntimeListView: filter options load failed', e)
        }
      })()
    }
  }, [schema?.filter_fields])

  const mergedFilters = useMemo(
    () => buildListFilters(schema, tabIndex, filterValues),
    [schema, tabIndex, filterValues],
  )
  // AND the create permission into the schema-derived CRUD-bar flag — every
  // Create affordance (toolbar button, hierarchical Group|Element menu, the
  // `n` keyboard shortcut below) reads `commandBar.hasCrudBar`, so this one
  // memo covers all three sites at once.
  const commandBar = useMemo(() => {
    const spec = commandBarFromSchema(schema)
    return { ...spec, hasCrudBar: spec.hasCrudBar && (permissions?.canCreate ?? true) }
  }, [schema, permissions?.canCreate])
  const canDeleteRows = !picking && (permissions?.canDelete ?? true)

  const flatListRef = useRef<FlatListViewHandle | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const [importOpen, setImportOpen] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [documentImportOpen, setDocumentImportOpen] = useState(false)
  const [documentImportFile, setDocumentImportFile] = useState<File | null>(null)
  const [importNotice, setImportNotice] = useState<string | null>(null)
  const importInputRef = useRef<HTMLInputElement>(null)

  // Hierarchy mode (Task 5): the currently selected tree row (group or element)
  // seeds parent_id for the next create; the Create control becomes a Group|Element
  // menu instead of a single button.
  const [hierarchySelected, setHierarchySelected] = useState<Record<string, unknown> | null>(null)
  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(null)

  // Column presentation: schema's Table node when present, else attribute-derived
  // (identical dedupe/skip rules to the pre-schema behavior). On the fallback path,
  // pass data_query.fields to subset the columns if specified.
  const visibleColumns: ListColumnSpec[] = useMemo(() => {
    const fromSchema = columnsFromSchema(schema)
    const base = fromSchema ?? (config ? columnsFromAttributes(config.attributes, schema?.data_query?.fields) : [])
    // Resolve locale label once here — every downstream consumer (ColumnsMenu,
    // flat/group/hierarchy table headers) inherits the resolved title for free.
    // A column with no synonyms of its own (custom list forms, or generated
    // schemas — ListFormGenerator never copies the map) borrows the matching
    // attribute's synonyms so translations still apply.
    return base.map((c) => {
      const merged = mergeAttributeSynonyms(c, config?.attributes)
      return {
        ...merged,
        title: resolveLabel(merged, locale, defaultCode),
      }
    })
  }, [schema, config, locale, defaultCode])

  // Operator Lists v2.1 (Task 9): per-user order/width/hidden layered over the
  // schema/attribute-derived `visibleColumns`. Depend on the stable `applyTo`
  // (useCallback keyed by prefs), not the hook object (fresh each render) —
  // otherwise the memo never memoizes.
  const columnPrefs = useColumnPrefs(connectionId, objectName)
  // Depend on stable applyTo (useCallback), not the hook object (new each render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const effectiveColumns = useMemo(() => columnPrefs.applyTo(visibleColumns), [columnPrefs.applyTo, visibleColumns])
  // Hierarchy parent links are structural — never surface as a data column.
  const tableColumns = useMemo(
    () => effectiveColumns.filter((c) => c.name !== 'parent_id'),
    [effectiveColumns],
  )
  // Search ORs across currently visible columns — never schema.search_fields
  // (stale Designer-authored lists diverge from prefs/hidden columns).
  const searchFields = useMemo(
    () => tableColumns.map((c) => c.name),
    [tableColumns],
  )
  // Kind/lock icons always ride the first *visible* data column so a column
  // reorder never parks them mid-table (presentation_field is display-only).
  const kindIconColumn = tableColumns[0]?.name ?? null
  const handleReorderColumn = useCallback((fromName: string, toName: string) => {
    const names = tableColumns.map((c) => c.name)
    const from = names.indexOf(fromName), to = names.indexOf(toName)
    if (from < 0 || to < 0) return
    names.splice(to, 0, names.splice(from, 1)[0])
    // Persist the FULL base order (include hidden columns so they keep their slot).
    const full = names.concat(visibleColumns.map((c) => c.name).filter((n) => !names.includes(n)))
    columnPrefs.setOrder(full)
  }, [tableColumns, visibleColumns, columnPrefs])

  const attrByName = useMemo(() => {
    const m = new Map<string, AttributeDef>()
    for (const a of config?.attributes ?? []) m.set(a.name.toLowerCase(), a)
    return m
  }, [config])

  // Composite-attribute filters (Task 8): a `{name}_type` select is generated
  // from `config.attributes` for every composite column and merged with any
  // schema-declared filter_fields (declared entries win on a name collision —
  // none expected, the composite name is synthesized). `compositeFilterMeta`
  // is the render loop's guard against a user's OWN `foo_type` column being
  // mistaken for a composite discriminator — only a `_type`-suffixed field
  // whose base attribute is genuinely `composite_ref` in config gets the
  // special Select+value-picker treatment.
  const effectiveFilterFields = useMemo(() => {
    const declared = schema?.filter_fields ?? []
    const seen = new Set(declared.map((f) => f.name))
    const generated = compositeFilterFields(config?.attributes).filter((f) => !seen.has(f.name))
    return [...declared, ...generated]
  }, [schema?.filter_fields, config?.attributes])

  const compositeFilterMeta = useMemo(() => {
    const rec: Record<string, { baseName: string; refTargets: string[]; primitives: string[] }> = {}
    for (const ff of effectiveFilterFields) {
      if (!ff.name.endsWith('_type')) continue
      const baseName = ff.name.slice(0, -'_type'.length)
      const baseAttr = attrByName.get(baseName.toLowerCase())
      if (!baseAttr || baseAttr.attr_type !== 'composite_ref') continue
      const { refTargets, primitives } = splitCompositeTypes(baseAttr.props?.composite_types)
      rec[ff.name] = { baseName, refTargets, primitives: Array.from(primitives) }
    }
    return rec
  }, [effectiveFilterFields, attrByName])

  const hintFor = useCallback(
    (col: ListColumnSpec): ListCellAttrHint | undefined => {
      const attr = attrByName.get(col.name.toLowerCase())
      return attr
        ? {
            name: attr.name,
            attr_type: attr.attr_type,
            props: attr.props,
            translatable: attr.translatable,
            locale: locale ?? '',
          }
        : undefined
    },
    [attrByName, locale],
  )

  // Inline cell editing (Operator Lists v2.1, Task 12): the writable attr behind a
  // column — id/is_deleted/tabular columns are never editable. Also gated off when
  // the schema declares a click_route: that list's click already navigates the
  // Operator to a dedicated record page, so in-grid edit would fight the same click.
  const attrFor = useCallback(
    (col: ListColumnSpec): AttributeDef | undefined => {
      const a = attrByName.get(col.name.toLowerCase())
      if (!a || isAppHiddenAttr(a) || a.attr_type === 'tabular') return undefined
      // Translatable columns render the locale-resolved sibling (hintFor) but
      // the base attribute's own value lives under a different key — inline
      // edit would seed from and commit to the wrong field (finding I1).
      // Editing translations stays in the record-form popover.
      if (a.translatable) return undefined
      return a
    },
    [attrByName],
  )

  // Underlying attributes for the visible columns — feeds kanban/calendar/grouping helpers
  // that still operate on AttributeDef. Without a schema this reproduces the exact
  // dedupe/skip list the pre-schema `visibleColumns` used to be.
  const visibleAttrs: AttributeDef[] = useMemo(() => {
    const names = new Set(visibleColumns.map((c) => c.name.toLowerCase()))
    const seen = new Set<string>()
    const out: AttributeDef[] = []
    for (const a of config?.attributes ?? []) {
      const key = a.name.toLowerCase()
      if (!names.has(key) || seen.has(key)) continue
      seen.add(key)
      out.push(a)
    }
    return out
  }, [config, visibleColumns])

  const kanbanFields = useMemo(() => kanbanCandidates(visibleAttrs), [visibleAttrs])
  const views = useMemo(
    () => availableViews(visibleAttrs, { hierarchical }),
    [visibleAttrs, hierarchical],
  )
  const migratedView = migrateViewKind(prefs.view, hierarchical)
  const storedView: ListViewKind = views.includes(migratedView)
    ? migratedView
    : hierarchical
      ? 'tree'
      : 'list'
  // A search term flattens tree view — see listViewMode.ts.
  const activeView: ListViewKind = resolveActiveListView(storedView, search)

  const titleField = useMemo(() => {
    if (visibleAttrs.some((a) => a.name === 'name')) return 'name'
    return visibleAttrs.find((a) => a.attr_type === 'string')?.name ?? 'id'
  }, [visibleAttrs])

  // Prefer a candidate other than the title field for the default column grouping
  // (grouping cards by their own unique title produces a useless one-card-per-column board).
  const defaultKanbanField = kanbanFields.find((f) => f.name !== titleField)?.name ?? kanbanFields[0]?.name ?? ''
  const kanbanField = kanbanFields.some((f) => f.name === prefs.kanbanField)
    ? prefs.kanbanField
    : defaultKanbanField
  const groupByPalette = useMemo(
    () => visibleColumns.map((c) => ({ name: c.name, title: c.title })),
    [visibleColumns],
  )
  const groupByFields = useMemo(() => {
    const names = groupByPalette.map((f) => f.name)
    const stored = prefs.groupByFields
    const base = stored.length > 0 ? stored : (schema?.data_query?.grouping ?? [])
    return dropStale(base, names)
  }, [prefs.groupByFields, schema?.data_query?.grouping, groupByPalette])
  // Hierarchy mode wins over group_by while traits.hierarchical is on (spec §2.1,
  // v1 — group_by toolbar hidden below, so groupByFields should stay empty anyway).
  const groupedMode = activeView === 'list' && !hierarchical && groupByFields.length > 0

  // Saved views: applying a view replaces search/filters/view/grouping wholesale;
  // the "All" pseudo-tab (view === null) clears back to defaults. Saving captures
  // the current list state as a new named snapshot.
  const handleApplySavedView = (view: SavedView | null) => {
    // Reset the schema tab in both branches: the tab contributes a filter to
    // mergedFilters, so leaving a stale tab selected would intersect with the
    // restored (or cleared) view and restore a different row set than saved.
    setTabIndex(0)
    if (view) {
      setSearch(view.search)
      setFilterValues(view.filters)
      setSortField(view.sortField ?? '')
      setSortDir(view.sortDir ?? 'asc')
      updatePrefs({
        view: view.view,
        groupByFields: view.groupByFields,
        aggregates: view.aggregates,
        ...(view.kanbanField ? { kanbanField: view.kanbanField } : {}),
      })
      columnPrefs.setAll(view.columns)
      saved.setActive(view.id)
    } else {
      setSearch('')
      setFilterValues({})
      setSortField('')
      setSortDir('asc')
      updatePrefs({ view: hierarchical ? 'tree' : 'list', groupByFields: [], aggregates: [] })
      columnPrefs.reset()
      saved.setActive(null)
    }
  }

  // Removing the active saved view: drop back to the Default (cleared) state so
  // the list doesn't keep showing the removed view's filters while the bar
  // highlights "Default".
  const handleRemoveSavedView = (id: string) => {
    const wasActive = saved.activeId === id
    saved.remove(id)
    if (wasActive) handleApplySavedView(null)
  }

  // Load config (from _config_objects), then refresh attributes from designerTree.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { default: apiClient } = await import('@/utils/api')
        const url = `/v1/designer/config/objects/${encodeURIComponent(objectName)}?connection_id=${encodeURIComponent(connectionId)}`
        const resp = await apiClient.get(url, { validateStatus: (s) => s === 200 || s === 404 })
        if (cancelled) return
        if (resp.status === 200 && resp.data) {
          const cfg = resp.data as ConfigObject
          try {
            const formResp = await apiClient.get(
              `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/form/list`,
            )
            const body = formResp.data as any
            if (body?.attributes && Array.isArray(body.attributes)) {
              cfg.attributes = body.attributes
            }
          } catch {
            // Fall back to _config_objects attributes
          }
          setConfig(cfg)
        } else {
          setConfigError(
            `Object "${objectName}" not found. Open the Designer and save the configuration first.`,
          )
        }
      } catch {
        if (!cancelled) setConfigError('Failed to load object configuration')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [connectionId, objectName])

  // Object-level print discovery (no recordId) — row Print passes the row into runPrint.
  const print = usePrintCommands({
    connectionId,
    objectName,
  })
  const printPickerOptions = useMemo(
    () => print.commands.map(printCommandToOption),
    [print.commands],
  )

  const rowsApi = useWindowedRows({
    connectionId,
    objectName,
    enabled: !!config && activeView === 'list' && !groupedMode,
    search,
    sortField,
    sortDir,
    showDeleted,
    filters: mergedFilters,
    searchFields,
    chunkSize: CHUNK_SIZE,
    maxPagesInMemory: MAX_PAGES_IN_MEMORY,
    locale: locale ?? '',
  })

  // Lazy parent_id/is_group tree for hierarchical catalogs — tree view only.
  const hierarchy = useHierarchyTree({
    connectionId,
    objectName,
    enabled: !!config && hierarchical && activeView === 'tree',
    search,
    searchFields,
    showDeleted,
    sortField,
    sortDir,
    locale: locale ?? '',
  })

  const capped = useCappedRows({
    connectionId,
    objectName,
    enabled: !!config && activeView === 'kanban',
    search,
    sortField,
    sortDir,
    showDeleted,
    filters: mergedFilters,
    searchFields,
    locale: locale ?? '',
  })

  // Lazy server-side group tree (replaces the capped client-side grouping).
  const groupTree = useGroupTree({
    connectionId,
    objectName,
    enabled: !!config && groupedMode,
    levels: groupByFields,
    baseFilters: mergedFilters,
    search,
    searchFields,
    showDeleted,
    sortField,
    sortDir,
    locale: locale ?? '',
  })

  const { total, getRow, reload, loadingInitial, loadingMore, error, removeRow, patchRow } = rowsApi
  const isBusy = loadingInitial || loadingMore

  // Keep the windowed-cache row and the selection snapshot (if any) in lockstep:
  // `windowedRowCache.patchRow` replaces the cache's row object immutably, but
  // `useRowSelection` stores its own snapshot captured at select time — without
  // this, inline-edit/bulk-set-field writes leave `selection.selectedRows`
  // pointing at pre-edit objects (stale `handleExportSelected` + a Σ footer that
  // disagrees with the cells). `selection.patch` is a no-op for unselected rows.
  const patchRowAndSelection = useCallback(
    (id: string, updater: (row: Record<string, unknown>) => Record<string, unknown>) => {
      patchRow(id, updater)
      selection.patch(id, updater)
    },
    // selection.patch is stable; full `selection` would churn every select.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patchRow, selection.patch],
  )

  // Inline cell edit state machine (Operator Lists v2.1, Task 12) — flat list only.
  // Declared here (not right after `selection`, per the original plan) because it
  // needs `patchRow`, which `useWindowedRows` only produces once `rowsApi` exists.
  const inline = useInlineEdit({ connectionId, objectName, patchRow: patchRowAndSelection, onError: setImportNotice })

  // Σ aggregates footer (list view only, Task 4). Values are fetched from Task 1's
  // `aggregate=field:op[,...]` list mode over the SAME filtered/searched set as the
  // plain list, and re-fetched whenever the configured aggregates or the underlying
  // filter/search state change. `aggregateReloadTick` folds it into `reloadActive` so
  // CRUD saves / imports / manual refresh don't leave the footer's totals stale.
  const [aggregateValues, setAggregateValues] = useState<Record<string, number | string | null>>({})
  const [aggregateReloadTick, setAggregateReloadTick] = useState(0)
  const aggregateActive = !!config && activeView === 'list' && !groupedMode && prefs.aggregates.length > 0

  useEffect(() => {
    if (!aggregateActive) {
      setAggregateValues({})
      return
    }
    let cancelled = false
    runtimeApi
      .aggregate(connectionId, objectName, {
        aggregate: prefs.aggregates,
        filters: mergedFilters,
        search,
        search_fields: searchFields,
        include_deleted: showDeleted,
        locale: locale ?? '',
      })
      .then((r) => {
        if (!cancelled) setAggregateValues(r.data ?? {})
      })
      .catch(() => {
        if (!cancelled) setAggregateValues({})
      })
    return () => {
      cancelled = true
    }
  }, [
    aggregateActive,
    connectionId,
    objectName,
    prefs.aggregates,
    mergedFilters,
    search,
    showDeleted,
    searchFields,
    locale,
    aggregateReloadTick,
  ])

  // Selection-scoped Σ override (Operator Lists v2.1, Task 7): when rows are
  // selected, compute aggregates client-side over just the selection instead of
  // showing the server-fetched filtered-set totals — mirrors the same ops so
  // the numbers agree whether the source is client or server.
  const selectionAgg = useMemo(
    () =>
      selection.count > 0
        ? { count: selection.count, values: computeClientAggregates(selection.selectedRows, prefs.aggregates) }
        : undefined,
    [selection.count, selection.selectedRows, prefs.aggregates],
  )

  // Reload every row data source (windowed list + capped kanban + lazy group/hierarchy
  // trees + Σ aggregates) so CRUD saves, imports, and manual refresh never leave an
  // inactive view's data stale. Hierarchy `reload` is soft (keeps expansion).
  const reloadActive = useCallback(() => {
    reload()
    capped.reload()
    groupTree.reload()
    hierarchy.reload()
    setAggregateReloadTick((t) => t + 1)
  }, [reload, capped.reload, groupTree.reload, hierarchy.reload]) // eslint-disable-line react-hooks/exhaustive-deps -- stable .reload fns

  // Selection is scoped to the current filtered view — changing what's shown
  // clears it, so the header checkbox can't misrepresent state and bulk actions
  // never touch rows the user can no longer see.
  useEffect(() => {
    selection.clear()
    // selection.clear is stable; full `selection` would clear on every select.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.clear, mergedFilters, search, showDeleted])

  // Hierarchy selection is scoped the same way — a changed filter/search/object
  // can make the previously selected row disappear from the tree.
  useEffect(() => {
    setHierarchySelected(null)
  }, [objectName, mergedFilters, search, showDeleted])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  // --- CRUD handlers ---

  const handleImportFiles = useCallback((files: File[]) => {
    if (!canImport) {
      setImportNotice(
        objectKind === 'other'
          ? 'Import is not available for this object'
          : 'You do not have permission to import records here',
      )
      return
    }
    const route = classifyImportForObject(objectKind, files)
    if (route.kind === 'catalog-spreadsheet') {
      setImportFile(route.spreadsheet)
      setImportOpen(true)
    } else if (route.kind === 'document-file') {
      setDocumentImportFile(route.file)
      setDocumentImportOpen(true)
    } else if (route.kind === 'catalog-scans-rejected') {
      setImportNotice('Catalogs import from Excel only')
    } else if (route.kind === 'document-multi-rejected') {
      setImportNotice('Documents only accept exactly one file')
    } else if (route.kind === 'unsupported-object') {
      setImportNotice('Import is not available for this object')
    }
  }, [canImport, objectKind])

  // Exports the CURRENT view's visible columns over the filtered/searched set,
  // fetched fresh (not the sparse windowed cache) up to the same hard cap the
  // kanban/group views use. Same cell display as the table (`hintFor`
  // + `formatListCellValue`); reuses the import Snackbar for cap/error notices.
  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const resp = await runtimeApi.list(connectionId, objectName, buildExportListParams({
        filters: mergedFilters,
        search,
        searchFields,
        showDeleted,
        sortField,
        sortDir,
        limit: CAPPED_FETCH_LIMIT,
        locale: locale ?? '',
      }))
      const csv = exportRowsToCsv(resp.data, tableColumns, (col, row) =>
        exportCellValue(col, row, hintFor(col) ?? hintFromColumn(col)),
      )
      downloadCsv(`${objectName}.csv`, csv)
      if (resp.meta.total > CAPPED_FETCH_LIMIT) {
        setImportNotice(
          `Exported first ${CAPPED_FETCH_LIMIT.toLocaleString()} of ${resp.meta.total.toLocaleString()}`,
        )
      }
    } catch (e) {
      setImportNotice(e instanceof Error ? e.message : 'Failed to export records')
    } finally {
      setExporting(false)
    }
  }, [connectionId, objectName, mergedFilters, search, searchFields, showDeleted, sortField, sortDir, tableColumns, hintFor, locale])

  // Header checkbox: select ALL filtered rows (capped fetch mirroring handleExport's),
  // or clear the current selection if anything is already selected.
  const handleToggleSelectAll = useCallback(async () => {
    if (selection.count > 0) {
      selection.clear()
      return
    }
    try {
      const resp = await runtimeApi.list(connectionId, objectName, buildExportListParams({
        filters: mergedFilters,
        search,
        searchFields,
        showDeleted,
        sortField,
        sortDir,
        limit: CAPPED_FETCH_LIMIT,
        locale: locale ?? '',
      }))
      selection.selectMany(resp.data)
      if (resp.meta.total > CAPPED_FETCH_LIMIT) {
        setImportNotice(`Selected first ${CAPPED_FETCH_LIMIT.toLocaleString()} of ${resp.meta.total.toLocaleString()}`)
      }
    } catch (e) {
      setImportNotice(e instanceof Error ? e.message : 'Failed to select records')
    }
  }, [selection, connectionId, objectName, mergedFilters, search, searchFields, showDeleted, sortField, sortDir, locale])

  // Bulk actions on the current selection (Operator Lists v2.1, Task 6): export
  // reuses the same cellFor as the main export so CSV columns stay identical;
  // delete/set-field are optimistic per-row with rollback + a partial-failure
  // count surfaced through the existing import Snackbar.
  const handleExportSelected = useCallback(() => {
    const csv = exportRowsToCsv(selection.selectedRows, tableColumns, (col, row) =>
      exportCellValue(col, row, hintFor(col) ?? hintFromColumn(col)))
    downloadCsv(`${objectName}-selected.csv`, csv)
  }, [selection.selectedRows, tableColumns, hintFor, objectName])

  const handleBulkSetField = useCallback(async (field: string, value: unknown) => {
    setBulkBusy(true)
    const entries = selection.selectedRows.slice()
    let failed = 0
    for (const r of entries) {
      const id = String(r.id)
      patchRowAndSelection(id, (row) => ({ ...row, [field]: value }))
      try { await runtimeApi.update(connectionId, objectName, id, { [field]: value }) }
      catch { failed++; patchRowAndSelection(id, (row) => ({ ...row, [field]: r[field] })) }
    }
    setBulkBusy(false); reloadActive()
    if (failed) setImportNotice(`${failed} of ${entries.length} could not be updated`)
  }, [selection.selectedRows, connectionId, objectName, patchRowAndSelection, reloadActive])

  // Row checkbox click: plain click toggles one row; Shift-click selects the
  // contiguous range from the last-clicked row (rangeAnchor) to this one.
  const rangeAnchor = useRef<number>(-1)
  const handleRowToggleSelect = useCallback(
    (e: React.MouseEvent, index: number) => {
      const cell = getRow(index)
      if (cell.state !== 'loaded') return
      if (e.shiftKey && rangeAnchor.current >= 0) {
        const [lo, hi] = [Math.min(rangeAnchor.current, index), Math.max(rangeAnchor.current, index)]
        const rows: Record<string, unknown>[] = []
        for (let i = lo; i <= hi; i++) {
          const c = getRow(i)
          if (c.state === 'loaded') rows.push(c.row)
        }
        selection.selectMany(rows)
      } else {
        selection.toggle(String(cell.row.id), cell.row)
        rangeAnchor.current = index
      }
    },
    [getRow, selection],
  )

  // Hierarchy selection: toggles one row with loaded descendants included/excluded.
  // On select → include the row plus all its loaded descendants.
  // On deselect → exclude the row and all its loaded descendants.
  const handleHierarchyToggleSelect = useCallback(
    (e: React.MouseEvent, index: number) => {
      const entry = hierarchy.entries[index]
      if (!entry) return

      const rowId = String(entry.id)
      const isCurrentlySelected = selection.isSelected(rowId)

      if (isCurrentlySelected) {
        // Deselect: remove this row and all its loaded descendants
        const toDeselect = [rowId, ...hierarchy.loadedDescendantIds(rowId)]
        selection.deselectMany(toDeselect)
      } else {
        // Select: add this row and all its loaded descendants
        const descendants = hierarchy.loadedDescendantIds(rowId)
        const toSelect = [entry.record]
        
        // Find descendant records to select
        for (const descId of descendants) {
          const descEntry = hierarchy.entries.find(e => e.id === descId)
          if (descEntry) {
            toSelect.push(descEntry.record)
          }
        }
        
        selection.selectMany(toSelect)
      }
    },
    [hierarchy.entries, hierarchy.loadedDescendantIds, selection],
  )

  // Hierarchy select-all: select all currently visible records with their loaded descendants
  const handleHierarchySelectAll = useCallback(() => {
    if (selection.count > 0) {
      selection.clear()
      return
    }

    const allRecords: Array<Record<string, unknown>> = []
    const processedIds = new Set<string>()

    for (const entry of hierarchy.entries) {
      if (processedIds.has(entry.id)) continue
      
      allRecords.push(entry.record)
      processedIds.add(entry.id)

      // Add loaded descendants
      const descendants = hierarchy.loadedDescendantIds(entry.id)
      for (const descId of descendants) {
        if (processedIds.has(descId)) continue
        const descEntry = hierarchy.entries.find(e => e.id === descId)
        if (descEntry) {
          allRecords.push(descEntry.record)
          processedIds.add(descId)
        }
      }
    }

    selection.selectMany(allRecords)
  }, [hierarchy.entries, hierarchy.loadedDescendantIds, selection])

  // `kind` only matters in hierarchy mode (Group|Element seed); non-hierarchical
  // callers keep calling this with no argument, same as before Task 5.
  const handleCreate = (kind: HierarchyCreateKind = 'element') => {
    if (!onCreate) return
    const seed = hierarchical ? seedForCreate(kind, hierarchySelected) : undefined
    onCreate(seed)
  }

  const handleEdit = (row: Record<string, unknown> | null) => {
    if (!row) return
    if (selectionMode) {
      selectionMode.onPick(String(row.id), row)
      return
    }
    if (schema?.click_route) {
      navigate(schema.click_route.replace('{id}', String(row.id)))
      return
    }
    if (!onOpen) return
    onOpen(String(row.id))
  }

  // Keyboard-first navigation (Operator Lists v2, Task 7) — active only for the
  // flat list view; the hook itself already ignores keys while typing in an
  // input/textarea/select/contentEditable, so it's safe to keep attached even
  // when other views are showing (they don't render the table container).
  const kbd = useListKeyboardNav({
    rowCount: total,
    onOpen: (index) => {
      const row = getRow(index)
      if (row.state === 'loaded') handleEdit(row.row)
    },
    // Gate `n` (new record) on the schema's CRUD bar, so a read-only list
    // schema (no Create button) doesn't open the create dialog via keyboard.
    // `n` always seeds an Element (spec §2.5) — Group is menu-only.
    onNew: commandBar.hasCrudBar ? () => handleCreate('element') : () => undefined,
    onFocusSearch: () => searchInputRef.current?.focus(),
    onToggleSelect: (index) => {
      const c = getRow(index)
      if (c.state === 'loaded') selection.toggle(String(c.row.id), c.row)
    },
  })

  // Keep the active keyboard row in view: without this the accent row walks
  // below the fold (arrow keys preventDefault native scroll) and Enter no-ops
  // on rows outside the windowed cache.
  useEffect(() => {
    if (activeView === 'list' && !groupedMode && kbd.activeIndex >= 0) {
      flatListRef.current?.scrollToIndex(kbd.activeIndex, { align: 'auto' })
    }
  }, [kbd.activeIndex, activeView, groupedMode])

  // Best-effort lookup across the data sources this view can render from, so
  // `handleDelete` can refuse a predefined row even if it's reached some way
  // other than the (already hidden) ListDataRow Delete button. A miss is not
  // treated as predefined — this is a backstop, not the source of truth.
  const findKnownRow = useCallback((id: string): Record<string, unknown> | undefined => {
    const inCapped = capped.rows.find((r) => String(r.id) === id)
    if (inCapped) return inCapped
    const inHierarchy = hierarchy.entries.find((e) => e.kind === 'row' && String(e.id) === id)
    return inHierarchy?.record
  }, [capped.rows, hierarchy.entries])

  // Soft-mark / drop a row without `reload()` (avoids loadingInitial → skeleton flash).
  const applyLocalDelete = useCallback(
    (recordId: string, wasMarked: boolean) => {
      if (showDeleted && !wasMarked) {
        const mark = (row: Record<string, unknown>) => ({ ...row, is_deleted: true })
        patchRow(recordId, mark)
        selection.patch(recordId, mark)
        capped.mutateRow(recordId, { is_deleted: true })
        const known = findKnownRow(recordId)
        if (known) hierarchy.patchRecord(recordId, { ...known, is_deleted: true, id: recordId })
        if (groupedMode) groupTree.reload()
      } else {
        removeRow(recordId)
        selection.deselectMany([recordId])
        capped.removeRow(recordId)
        hierarchy.removeRecord(recordId)
        if (groupedMode) groupTree.reload()
      }
      setAggregateReloadTick((t) => t + 1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selection mutators are stable
    [
      showDeleted,
      patchRow,
      selection.patch,
      selection.deselectMany,
      capped.mutateRow,
      capped.removeRow,
      findKnownRow,
      hierarchy.patchRecord,
      hierarchy.removeRecord,
      groupedMode,
      groupTree.reload,
      removeRow,
    ],
  )

  // Form tab → list: bare notify (save) still full-reloads; delete/undelete soft-update.
  useEffect(
    () =>
      subscribeRecordsChanged(objectName, (detail) => {
        if (detail?.reason === 'delete' && detail.recordId) {
          applyLocalDelete(detail.recordId, Boolean(detail.wasMarked))
          return
        }
        if (detail?.reason === 'undelete' && detail.recordId) {
          if (showDeleted) {
            patchRow(detail.recordId, (row) => ({ ...row, is_deleted: false }))
            capped.mutateRow(detail.recordId, { is_deleted: false })
            const known = findKnownRow(detail.recordId)
            if (known) {
              hierarchy.patchRecord(detail.recordId, { ...known, is_deleted: false, id: detail.recordId })
            }
            setAggregateReloadTick((t) => t + 1)
          } else {
            reloadActive()
          }
          return
        }
        reloadActive()
      }),
    [
      objectName,
      applyLocalDelete,
      showDeleted,
      patchRow,
      capped.mutateRow,
      findKnownRow,
      hierarchy.patchRecord,
      reloadActive,
    ],
  )

  const handleDelete = async (recordId: string, wasMarked?: boolean) => {
    if (!canDeleteRows) {
      setImportNotice('You do not have permission to delete records')
      return
    }
    const known = findKnownRow(recordId)
    if (known && isPredefinedRecord(known)) {
      setImportNotice('Cannot delete predefined records')
      return
    }
    const marked =
      wasMarked === true
      || known?.is_deleted === true
      || known?.is_deleted === 1
    try {
      // Hierarchy mode: use hierarchyDeleteConfirmMessage, flat mode: no new confirm (per spec)
      if (hierarchical && !marked) {
        if (!(await confirmAsync(hierarchyDeleteConfirmMessage(1)))) return
      }
      
      const response = await runtimeApi.delete(connectionId, objectName, recordId)
      applyLocalDelete(recordId, Boolean(marked))
      
      // Handle cascaded deletions in hierarchy mode
      if (hierarchical && response && Array.isArray(response.cascaded_ids)) {
        for (const cascadedId of response.cascaded_ids) {
          hierarchy.removeRecord(cascadedId)
          selection.deselectMany([cascadedId])
        }
      }
      
      // Show notice if predefined records were skipped
      if (response?.skipped_predefined && response.skipped_predefined > 0) {
        setImportNotice(`${response.skipped_predefined} predefined record(s) were skipped during cascade delete`)
      }
    } catch (e) {
      setImportNotice(e instanceof Error ? e.message : 'Failed to delete record')
    }
  }

  const handleBulkDelete = useCallback(async () => {
    if (!canDeleteRows) {
      setImportNotice('You do not have permission to delete records')
      return
    }
    // Predefined (Designer-seeded) rows are Designer-only to delete — the UI
    // hide on ListDataRow is primary; this is the bulk-path backstop so a
    // predefined row can never ride along in a multi-select delete.
    const rows = selection.selectedRows
    const deletable = rows.filter((r) => !isPredefinedRecord(r))
    const skipped = rows.length - deletable.length
    if (deletable.length === 0) {
      setImportNotice('Cannot delete predefined records')
      return
    }
    
    // Hierarchy mode: use hierarchyDeleteConfirmMessage and filter selection roots
    if (hierarchical) {
      const roots = filterSelectionRoots(deletable as SelectionRow[])
      if (!(await confirmAsync(hierarchyDeleteConfirmMessage(roots.length)))) return
      
      setBulkBusy(true)
      let failed = 0
      let totalSkippedPredefined = 0
      
      for (const row of roots) {
        const id = String(row.id)
        const wasMarked = row.is_deleted === true || row.is_deleted === 1
        try {
          const response = await runtimeApi.delete(connectionId, objectName, id)
          applyLocalDelete(id, Boolean(wasMarked))
          
          // Handle cascaded deletions in hierarchy mode
          if (response && Array.isArray(response.cascaded_ids)) {
            for (const cascadedId of response.cascaded_ids) {
              hierarchy.removeRecord(cascadedId)
              selection.deselectMany([cascadedId])
            }
          }
          
          // Accumulate skipped predefined counts
          if (response?.skipped_predefined) {
            totalSkippedPredefined += response.skipped_predefined
          }
        } catch {
          failed++
        }
      }
      selection.clear()
      setBulkBusy(false)
      
      const notices: string[] = []
      if (failed) notices.push(`${failed} of ${roots.length} could not be deleted`)
      if (skipped) notices.push(`${skipped} predefined record(s) skipped`)
      if (totalSkippedPredefined) notices.push(`${totalSkippedPredefined} predefined record(s) were skipped during cascade delete`)
      if (notices.length) setImportNotice(notices.join('; '))
    } else {
      // Flat mode: unchanged behavior
      if (!(await confirmAsync(deleteConfirmMessage(deletable)))) return
      setBulkBusy(true)
      let failed = 0
      for (const row of deletable) {
        const id = String(row.id)
        const wasMarked = row.is_deleted === true || row.is_deleted === 1
        try {
          await runtimeApi.delete(connectionId, objectName, id)
          applyLocalDelete(id, Boolean(wasMarked))
        } catch {
          failed++
        }
      }
      selection.clear()
      setBulkBusy(false)
      const notices: string[] = []
      if (failed) notices.push(`${failed} of ${deletable.length} could not be deleted`)
      if (skipped) notices.push(`${skipped} predefined record(s) skipped`)
      if (notices.length) setImportNotice(notices.join('; '))
    }
  }, [selection, connectionId, objectName, applyLocalDelete, canDeleteRows, hierarchical, hierarchy])

  const handleUndelete = async (recordId: string) => {
    try {
      await runtimeApi.undelete(connectionId, objectName, recordId)
      if (showDeleted) {
        patchRow(recordId, (row) => ({ ...row, is_deleted: false }))
        capped.mutateRow(recordId, { is_deleted: false })
        const known = findKnownRow(recordId)
        if (known) hierarchy.patchRecord(recordId, { ...known, is_deleted: false, id: recordId })
        setAggregateReloadTick((t) => t + 1)
      } else {
        reloadActive()
      }
    } catch {
      // ignore
    }
  }

  const kanbanAttr = visibleAttrs.find((a) => a.name === kanbanField)
  const kanbanLabelFor = useCallback(
    (value: unknown, sampleRow: Record<string, unknown>) =>
      kanbanAttr ? formatListCellValue(kanbanAttr, sampleRow) : String(value),
    [kanbanAttr],
  )

  const handleKanbanMove = async (rowId: string, newValue: unknown) => {
    const prev = capped.rows.find((r) => String(r.id) === rowId)
    if (!prev) return
    const prevValue = prev[kanbanField]
    capped.mutateRow(rowId, { [kanbanField]: newValue })
    try {
      await runtimeApi.update(connectionId, objectName, rowId, { [kanbanField]: newValue })
    } catch (e) {
      capped.mutateRow(rowId, { [kanbanField]: prevValue })
      setMoveError(e instanceof Error ? e.message : 'Failed to move record')
    }
  }

  const numericFields = useMemo(
    () =>
      visibleAttrs
        .filter((a) => a.attr_type === 'number')
        .map((a) => ({
          name: a.name,
          synonym: a.synonym || a.name,
          precision: typeof a.props?.precision === 'number' ? a.props.precision : undefined,
          scale: typeof a.props?.scale === 'number' ? a.props.scale : undefined,
        })),
    [visibleAttrs],
  )

  const extraCommands: CommandItem[] = [
    ...(canImport
      ? [{
          id: 'Import',
          label: 'Import',
          icon: <FileUploadIcon />,
          onClick: () => importInputRef.current?.click(),
        } satisfies CommandItem]
      : []),
    ...(!picking
      ? [
          {
            id: 'Export',
            label: 'Export',
            icon: <FileDownloadIcon />,
            onClick: handleExport,
            disabled: exporting,
            busy: exporting,
          } satisfies CommandItem,
          {
            id: 'ShowDeleted',
            label: showDeleted ? 'Hide deleted' : 'Show deleted',
            icon: <DeleteOutlineIcon />,
            onClick: () => setShowDeleted((v) => !v),
          } satisfies CommandItem,
        ]
      : []),
    {
      id: 'Refresh',
      label: 'Refresh',
      icon: <RefreshIcon />,
      onClick: reloadActive,
    } satisfies CommandItem,
  ]
  const pinKey = commandPinKey(connectionId, objectName, 'MainCommandBar')
  const pinPrefs = parsePinPrefs(pinRevision >= 0 ? prefsStore.getItem(pinKey) : null)
  const extrasSecondary: CommandItem[] = []
  const extrasOverflow: CommandItem[] = []
  const locked = LOCKED_PRIMARY_IDS as readonly string[]
  for (const item of extraCommands) {
    const id = item.id ?? item.label
    const slot = resolvePinSlot(id, undefined, pinPrefs)
    if (slot === 'overflow') extrasOverflow.push(item)
    else if (!locked.includes(id)) extrasSecondary.push(item)
  }
  const chromePins = Object.fromEntries(
    LIST_CHROME_IDS.map((id) => [id, resolvePinSlot(id, 'overflow', pinPrefs)]),
  ) as Record<ListChromeId, PinSlot>
  const listSettingsValue: ListSettingsValue = {
    view: storedView,
    availableViews: views,
    density,
    filters: filterValues,
    groupByFields: hierarchical ? [] : groupByFields,
    groupByPalette: hierarchical ? [] : groupByPalette,
    columns: visibleColumns.map((c) => ({ name: c.name, title: c.title })),
    columnPrefs: columnPrefs.prefs,
    pins: chromePins,
  }
  const handlePin = (id: string, slot: PinSlot) => {
    const current = parsePinPrefs(prefsStore.getItem(pinKey)) ?? { pinned: [], overflow: [] }
    const next =
      slot === 'bar'
        ? {
            pinned: [...current.pinned.filter((x) => x !== id), id],
            overflow: current.overflow.filter((x) => x !== id),
          }
        : {
            pinned: current.pinned.filter((x) => x !== id),
            overflow: [...current.overflow.filter((x) => x !== id), id],
          }
    prefsStore.setItem(pinKey, JSON.stringify(next))
    setPinRevision((n) => n + 1)
  }
  const handleResetPins = () => {
    prefsStore.removeItem(pinKey)
    setPinRevision((n) => n + 1)
  }

  // Flat list uses shared FlatListView (same component as Designer Predefined).
  // Tree mode uses RuntimeHierarchyTreeView; kind icons always host on columns[0].
  return (
    <FileDropOverlay enabled={!picking} onFiles={handleImportFiles} overlayText={`Drop files to import into ${objectName}`} sx={{ height: '100%' }}>
    <BaseFormShell
      title={resolveLabel({ synonyms: config?.synonyms, synonym: config?.synonym, name: objectName }, locale, defaultCode)}
      hideTitle={hideShellTitle}
      fullHeight
    >
    <ListSurface 
      fill
      aria-label={`${resolveLabel({ synonyms: config?.synonyms, synonym: config?.synonym, name: objectName }, locale, defaultCode)} list`}
      footer={
        activeView === 'list' && !groupedMode ? (
          <ListAggregatesFooter
            recordCount={total}
            aggregates={prefs.aggregates}
            values={aggregateValues}
            numericFields={numericFields.map((f) => ({
              name: f.name,
              label: f.synonym,
              precision: f.precision,
              scale: f.scale,
            }))}
            onChange={(next) => updatePrefs({ aggregates: next })}
            layout={prefs.aggregatesLayout}
            onLayoutChange={(next) => updatePrefs({ aggregatesLayout: next })}
            fieldOrder={prefs.aggregatesFieldOrder}
            onFieldOrderChange={(next) => updatePrefs({ aggregatesFieldOrder: next })}
            scopeOverride={selectionAgg}
          />
        ) : null
      }
      toolbar={
        <>
          {!picking && selection.count > 0 && (
            <ListSelectionBar
              count={selection.count}
              connectionId={connectionId}
              busy={bulkBusy}
              setFieldAttrs={visibleAttrs.filter((a) => !isAppHiddenAttr(a) && a.attr_type !== 'tabular')}
              onClear={selection.clear}
              onExport={handleExportSelected}
              onDelete={handleBulkDelete}
              onSetField={handleBulkSetField}
            />
          )}
          <ListToolbar
            embedded
            secondary={extrasSecondary}
            overflow={extrasOverflow}
            onPin={handlePin}
            onResetPins={handleResetPins}
        busy={isBusy || capped.loading || groupTree.loading || (activeView === 'tree' && hierarchy.loading)}
        primaryAction={commandBar.hasCrudBar ? {
          label: 'Create',
          icon: <AddIcon />,
          onClick: hierarchical
            ? (e) => setCreateMenuAnchor(e.currentTarget)
            : () => handleCreate('element'),
        } : undefined}
        search={commandBar.hasSearch ? { value: searchInput, onChange: setSearch } : undefined}
        searchInputRef={searchInputRef}
        density={{ value: density, onChange: setDensity }}
        chromePins={chromePins}
        onOpenSettings={() => openSettings()}
        view={{ value: activeView, onChange: (v) => updatePrefs({ view: v }), available: views }}
        filters={
          activeView === 'kanban' && kanbanFields.length > 1 ? (
            <TextField
              select
              size="small"
              label="Columns"
              value={kanbanField}
              onChange={(e) => updatePrefs({ kanbanField: e.target.value })}
              sx={{ width: 160 }}
            >
              {kanbanFields.map((f) => (
                <MenuItem key={f.name} value={f.name}>{f.synonym || f.name}</MenuItem>
              ))}
            </TextField>
          ) : undefined
        }
        filterPicker={
          effectiveFilterFields.length > 0 ? (
            <Button
              size="small"
              variant="outlined"
              aria-label="Filters"
              onClick={() => openSettings('filters')}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {Object.values(filterValues).filter(Boolean).length
                ? `Filters (${Object.values(filterValues).filter(Boolean).length})`
                : 'Filters'}
            </Button>
          ) : undefined
        }
        fieldPickers={
          activeView === 'list' && !hierarchical && groupByPalette.length > 0 ? (
            <GroupBySettings
              fields={groupByPalette}
              value={groupByFields}
              onChange={() => {}}
              onOpen={() => openSettings('grouping')}
            />
          ) : undefined
        }
        actions={
          <ColumnsMenu
            columns={visibleColumns.map((c) => ({ name: c.name, title: c.title }))}
            hidden={columnPrefs.prefs.hidden}
            onToggle={columnPrefs.toggleHidden}
            onReset={columnPrefs.reset}
            onOpen={() => openSettings('columns')}
          />
        }
        barActions={
          <ColumnsMenu
            iconOnly
            columns={visibleColumns.map((c) => ({ name: c.name, title: c.title }))}
            hidden={columnPrefs.prefs.hidden}
            onToggle={columnPrefs.toggleHidden}
            onReset={columnPrefs.reset}
            onOpen={() => openSettings('columns')}
          />
        }
      />
      {settingsOpen && (
        <ListSettingsDialog
          open
          initialTab={settingsTab}
          value={listSettingsValue}
          draftEpoch={settingsEpoch}
          onClose={() => setSettingsOpen(false)}
          onApply={(next) => {
            updatePrefs({ view: next.view, groupByFields: next.groupByFields })
            setDensity(next.density)
            setFilterValues(next.filters)
            columnPrefs.setAll(next.columnPrefs)
            for (const id of LIST_CHROME_IDS) {
              if (next.pins[id] !== chromePins[id]) handlePin(id, next.pins[id])
            }
            setSettingsOpen(false)
          }}
          filterFields={effectiveFilterFields}
          filterOptionsByField={filterOptionsByField}
          compositeFilterMeta={compositeFilterMeta}
          connectionId={connectionId}
          onTabChange={(tab) => {
            lastSettingsTab.current = tab
          }}
          savedViews={saved.views}
          activeViewId={saved.activeId}
          onApplySavedView={(view) => {
            handleApplySavedView(view)
            setSettingsEpoch((n) => n + 1)
          }}
          onRemoveSavedView={handleRemoveSavedView}
          onSaveView={(name, snapshot) => {
            saved.save(name, {
              search: searchInput,
              filters: snapshot.filters,
              view: snapshot.view,
              groupByFields: snapshot.groupByFields,
              aggregates: prefs.aggregates,
              sortField,
              sortDir,
              kanbanField: prefs.kanbanField,
              columns: snapshot.columnPrefs,
            })
          }}
        />
      )}
      {canImport && (
        <input
          ref={importInputRef}
          type="file"
          multiple
          accept={IMPORT_ACCEPT}
          hidden
          onChange={(e) => {
            const list = e.target.files ? Array.from(e.target.files) : []
            e.target.value = ''
            if (list.length) handleImportFiles(list)
          }}
        />
      )}
      {hierarchical && commandBar.hasCrudBar && (
        <Menu
          anchorEl={createMenuAnchor}
          open={!!createMenuAnchor}
          onClose={() => setCreateMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setCreateMenuAnchor(null)
              handleCreate('group')
            }}
          >
            Group
          </MenuItem>
          <MenuItem
            onClick={() => {
              setCreateMenuAnchor(null)
              handleCreate('element')
            }}
          >
            Element
          </MenuItem>
        </Menu>
      )}
          {(error || configError || groupTree.error) && (
            <Alert severity="error" sx={{ m: 1 }}>
              {error || configError || groupTree.error}
            </Alert>
          )}

          {degraded && (
            <Alert severity="warning" sx={{ m: 1 }}>
              Custom form failed to load, showing default
            </Alert>
          )}

          {!!schema?.tabs?.length && (
            <ListFormTabsBar tabs={schema.tabs} tabIndex={tabIndex} onTabChange={setTabIndex} />
          )}
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, flex: 1 }}>
        {activeView === 'kanban' ? (
        <>
          {capped.capped && (
            <Alert severity="info" sx={{ mx: 1.5, mt: 1 }}>
              Showing first {CAPPED_FETCH_LIMIT.toLocaleString()} of {capped.total.toLocaleString()} records
            </Alert>
          )}
          <KanbanView
            rows={capped.rows}
            columnField={kanbanField}
            titleField={titleField}
            subtitleField={numericFields[0]?.name}
            labelFor={kanbanLabelFor}
            onOpen={(id) => handleEdit(capped.rows.find((r) => String(r.id) === id) ?? null)}
            onMove={picking ? undefined : handleKanbanMove}
          />
        </>
      ) : activeView === 'tree' ? (
        <Box
          data-testid="hierarchy-tree-container"
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}
          tabIndex={0}
          onKeyDown={kbd.handlers.onKeyDown}
        >
          <RuntimeHierarchyTreeView
            entries={hierarchy.entries}
            columns={tableColumns}
            hintFor={hintFor}
            rowHeight={rowHeight}
            toggle={hierarchy.toggle}
            print={print}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onUndelete={handleUndelete}
            loading={hierarchy.loading}
            error={hierarchy.error}
            selectedId={hierarchySelected ? String((hierarchySelected as { id?: unknown }).id ?? '') : null}
            onSelect={setHierarchySelected}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            onResize={columnPrefs.setWidth}
            onResetWidth={(name) => columnPrefs.setWidth(name, 0)}
            onReorder={handleReorderColumn}
            selectable={!picking}
            isSelected={(id) => selection.isSelected(id)}
            onToggleSelect={handleHierarchyToggleSelect}
            selectAllChecked={hierarchy.entries.length > 0 && selection.count > 0 && hierarchy.entries.every(entry => selection.isSelected(String(entry.id)))}
            selectAllIndeterminate={selection.count > 0 && !hierarchy.entries.every(entry => selection.isSelected(String(entry.id)))}
            onToggleSelectAll={handleHierarchySelectAll}
            kindIconColumn={kindIconColumn}
            canDelete={canDeleteRows}
          />
        </Box>
      ) : groupedMode ? (
        <RuntimeGroupTreeView
          entries={groupTree.entries}
          columns={tableColumns}
          hintFor={hintFor}
          rowHeight={rowHeight}
          toggle={groupTree.toggle}
          rowAt={groupTree.rowAt}
          groupValues={groupTree.groupValues}
          print={print}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onUndelete={handleUndelete}
          canDelete={canDeleteRows}
        />
      ) : (
        <FlatListView
          ref={flatListRef}
          count={total}
          getRow={getRow}
          columns={tableColumns}
          hintFor={hintFor}
          rowHeight={rowHeight}
          print={print}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onUndelete={handleUndelete}
          loading={loadingInitial}
          selectable={!picking}
          isSelected={(id) => selection.isSelected(id)}
          onToggleSelect={handleRowToggleSelect}
          selectAllChecked={total > 0 && selection.count >= total}
          selectAllIndeterminate={selection.count > 0 && selection.count < total}
          onToggleSelectAll={handleToggleSelectAll}
          activeIndex={kbd.activeIndex}
          onKeyDown={kbd.handlers.onKeyDown}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
          onResize={columnPrefs.setWidth}
          onResetWidth={(name) => columnPrefs.setWidth(name, 0)}
          onReorder={handleReorderColumn}
          editable={!picking && !schema?.click_route}
          connectionId={connectionId}
          attrFor={attrFor}
          editState={inline.editing}
          editDraft={inline.draft}
          onBeginEdit={inline.begin}
          onEditChange={inline.setDraft}
          onEditCommit={inline.commit}
          onEditCancel={inline.cancel}
          kindIconColumn={kindIconColumn}
          canDelete={canDeleteRows}
          emptyMessage={
            search.trim()
              ? `No records match "${search.trim()}"`
              : undefined
          }
        />
      )}
      </Box>
      </ListSurface>

      <PrintLayoutPickerMenu
        menu={print.layoutPickMenu}
        options={printPickerOptions}
        onClose={print.closeLayoutPickMenu}
        onPick={(opt: PrintLayoutOption, ctx) => {
          const cmd = print.commands.find((c) => c.id === String(opt.id))
          if (!cmd) return
          // v1: pdf mode shares the print-window path (export lives inside the window).
          void print.runPrint(ctx.record, cmd)
        }}
      />

      <ImportDialog
        open={importOpen}
        onClose={() => { setImportOpen(false); setImportFile(null) }}
        connectionId={connectionId}
        objectName={objectName}
        initialFile={importFile}
        onImported={reloadActive}
      />
      <DocumentImportDialog
        open={documentImportOpen}
        onClose={() => { setDocumentImportOpen(false); setDocumentImportFile(null) }}
        connectionId={connectionId}
        objectName={objectName}
        file={documentImportFile}
        onImported={reloadActive}
      />
      <Snackbar
        open={!!importNotice}
        autoHideDuration={5000}
        onClose={() => setImportNotice(null)}
        message={importNotice ?? ''}
      />
      <Snackbar
        open={!!moveError}
        autoHideDuration={5000}
        onClose={() => setMoveError(null)}
        message={moveError ?? ''}
      />
    </BaseFormShell>
    </FileDropOverlay>
  )
}
